import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import type { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import type { AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { PersonalAccountExternalUserService } from '@zaoblako/web/core/services/lk/personal-account-external-user.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { map } from 'rxjs/operators';

interface ExternalUserWithStatus extends ExternalUserFindInterface {
  activePassStatus?: string;
}

@Component({
  selector: 'crm-personal-account-external-user-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent],
  templateUrl: './personal-account-external-user-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalAccountExternalUserMultiSelectComponent implements OnInit {
  private static readonly ENTITY_CACHE_KIND = 'pa-external-user';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @ViewChild('multiSelectComponent', { static: true }) public readonly multiSelectComponent: MultiSelectComponent;

  @Input() public externalUsers: ExternalUserWithStatus[] = [];
  public queryParams: DropdownBaseQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public selectionLoading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedExternalUsers: ExternalUserFindInterface[] = [];
  @Input() public set selectedExternalUsers(selectedExternalUsers: ExternalUserFindInterface[] | any[]) {
    if (this.customSearch) {
      this._selectedExternalUsers = selectedExternalUsers;
      return;
    }

    if (
      selectedExternalUsers?.every((selectedUser) => this.externalUsers.some((user) => user.id === selectedUser.id))
    ) {
      this._selectedExternalUsers = this.externalUsers.filter(
        (user) => selectedExternalUsers?.some((selectedUser) => user.id === selectedUser.id)
      );
      return;
    }

    const ids = selectedExternalUsers
      ?.filter((selectedUser) => !this.externalUsers.some((user) => user.id === selectedUser.id))
      .map((selectedUser) => selectedUser.id);
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (ids?.length) {
      this.selectionLoading = true;
      this.cdRef.markForCheck();
      this.entityCache
        .loadByIds(PersonalAccountExternalUserMultiSelectComponent.ENTITY_CACHE_KIND, ids, (missing) =>
          this.personalAccountExternalUserService
            .findWithPagination({
              includeIds: missing as number[],
              limit: missing.length > 500 ? 500 : missing.length,
              offset: 0
            })
            .pipe(map((data) => this.mapUsersWithStatus(data.items)))
        )
        .subscribe({
          next: (items) => {
            this.externalUsers = this.externalUsers.concat(
              items.filter((item) => !this.externalUsers.some((user) => user.id === item.id))
            );
            this.cdRef.markForCheck();
            setTimeout(() => {
              this._selectedExternalUsers = this.externalUsers.filter((user) => ids.includes(user.id));
              this.selectionLoading = false;
              this.cdRef.markForCheck();
            }, 30);
          },
          error: () => {
            this.selectionLoading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  @Output() public selectedExternalUsersChange = new EventEmitter<ExternalUserFindInterface[]>();

  @Input() public isError = false;

  @Input() public label: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public showActivePassStatus = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedExternalUserIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (
      ids?.length &&
      ((this._selectedExternalUsers?.length &&
        !this._selectedExternalUsers?.every((selectedUser) => ids.includes(selectedUser.id))) ||
        !this._selectedExternalUsers?.length)
    ) {
      let newIds = ids.filter((id) => !this.externalUsers.some((user) => user.id === id));
      if (newIds?.length) {
        this.selectionLoading = true;
        this.cdRef.markForCheck();
        this.entityCache
          .loadByIds(PersonalAccountExternalUserMultiSelectComponent.ENTITY_CACHE_KIND, newIds, (missing) =>
            this.personalAccountExternalUserService
              .findWithPagination({
                includeIds: missing as number[],
                limit: missing.length > 500 ? 500 : missing.length,
                offset: 0
              })
              .pipe(map((data) => this.mapUsersWithStatus(data.items)))
          )
          .subscribe({
            next: (items) => {
              this.externalUsers = this.externalUsers.concat(
                items.filter((item) => !this.externalUsers.some((user) => user.id === item.id))
              );
              this.cdRef.markForCheck();
              setTimeout(() => {
                this.applySelectionFromRequestedIds(ids);
                this.selectionLoading = false;
                this.cdRef.markForCheck();
              }, 30);
            },
            error: () => {
              this.selectionLoading = false;
              this.onSelect([]);
              this.cdRef.markForCheck();
            }
          });
      } else {
        this.applySelectionFromRequestedIds(ids);
      }
    }
  }

  public constructor(
    private readonly personalAccountExternalUserService: PersonalAccountExternalUserService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      const fingerprint = this.entityCache.fingerprint({
        ...this.queryParams,
        showActivePassStatus: this.showActivePassStatus
      });

      this.entityCache
        .loadFirstPage(PersonalAccountExternalUserMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.personalAccountExternalUserService.findWithPagination(this.queryParams).pipe(
            map((data) => ({
              items: this.mapUsersWithStatus(data.items),
              count: data.count
            }))
          )
        )
        .subscribe({
          next: (data) => {
            this.externalUsers = this.externalUsers?.length
              ? this.externalUsers.concat(
                  data.items.filter((item) => !this.externalUsers.some((user) => user.id === item.id))
                )
              : data.items.filter((item) => !this.externalUsers.some((user) => user.id === item.id));
            this.count = data.count;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  public onSelect(users: ExternalUserFindInterface[]) {
    this.entityCache.putEntities(PersonalAccountExternalUserMultiSelectComponent.ENTITY_CACHE_KIND, users ?? []);
    this.selectedExternalUsersChange.emit(users);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.multiSelectComponent?.multiSelect?.scroller.onScroll.subscribe(() => {
          let el: HTMLElement = this.multiSelectComponent?.multiSelect?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.externalUsers.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.personalAccountExternalUserService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  const mappedItems = this.mapUsersWithStatus(data.items);
                  this.externalUsers = this.externalUsers.concat(
                    mappedItems.filter((item) => !this.externalUsers.some((user) => user.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.personalAccountExternalUserService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          const mappedItems = this.mapUsersWithStatus(data.items);
          this.externalUsers = mappedItems.concat(
            this._selectedExternalUsers?.filter(
              (selectedUser) => !data.items.some((item) => item.id === selectedUser.id)
            )
          );
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  private applySelectionFromRequestedIds(requestedIds: number[]): void {
    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.externalUsers, requestedIds);
    this._selectedExternalUsers = selected;
    if (hasStaleKeys) {
      this.onSelect(selected);
    }
    this.cdRef.markForCheck();
  }

  private mapUsersWithStatus(users: ExternalUserFindInterface[]): ExternalUserWithStatus[] {
    return users.map((user) => {
      const hasActive = user.accessCards?.some((card: AccessCardFindInterface) => card.isActive);
      return {
        ...user,
        activePassStatus: hasActive && this.showActivePassStatus ? 'Есть активный пропуск' : null
      };
    });
  }
}
